import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../kit/Sheet';
import { Button } from '../kit/Button';
import { haptic } from '../../lib/haptic';
import { TimeField } from './TimeField';
import { DateField } from './DateField';
import { PhotoCapture } from '../form/PhotoCapture';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type Props = {
  open: boolean;
  // The captured release reading — used to pre-fill and to guard the lower
  // bounds (End can't undercut the release odometer or predate the release).
  // km is null until the post-signature close-out captures it.
  release: { date: string; time: string; km: number | null };
  // Last-entered end values (default to the release reading until the driver
  // captures a distinct garage-in) — seeds the form when re-opened to edit.
  initial?: { date: string | null; time: string | null; km: number | null; dutySlipUrl?: string | null };
  onClose: () => void;
  onConfirm: (data: { endDate: string; endTime: string; endKm: number; dutySlipUrl: string | null }) => void;
  submitting?: boolean;
};

/**
 * Garage-in / end-of-duty capture, shown after a trip is released. Records the
 * odometer + time when the driver reached the garage on the return leg (which
 * can be later than the passenger drop / release). Feeds the operator's Final
 * Reporting "End" row, the duty slip, and garage-to-garage billing.
 *
 * Conditionally rendered by the parent (mounts fresh each open) so the useState
 * seeds from `initial`/`release` props every time it's opened.
 */
export function EndReadingSheet({ open, release, initial, onClose, onConfirm, submitting }: Props) {
  const { t } = useTranslation('trip');
  // Pre-fill from the last-entered end values, falling back to the release
  // reading (the garage-in is usually close to, and never before, the drop).
  const [km, setKm] = useState(String(initial?.km ?? release.km ?? ''));
  const [date, setDate] = useState(initial?.date || release.date || todayIso());
  const [time, setTime] = useState((initial?.time || release.time || '').slice(0, 5));
  const [dutySlipUrl, setDutySlipUrl] = useState<string | null>(initial?.dutySlipUrl ?? null);

  const kmValid = /^\d{3,6}$/.test(km);
  const timeValid = /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date);

  // End can't undercut the release odometer, nor predate the release moment.
  const kmOrderingError = kmValid && release.km != null && parseInt(km, 10) < release.km;
  const releaseMs = new Date(`${release.date}T${release.time}:00+05:30`).getTime();
  const endMs = dateValid && timeValid ? new Date(`${date}T${time}:00+05:30`).getTime() : NaN;
  const timeOrderingError = Number.isFinite(releaseMs) && Number.isFinite(endMs) && endMs < releaseMs;

  const isValid = kmValid && timeValid && dateValid && !kmOrderingError && !timeOrderingError;

  const handleSubmit = () => {
    if (!isValid) return;
    haptic.tap();
    onConfirm({ endKm: parseInt(km, 10), endDate: date, endTime: time, dutySlipUrl });
  };

  const inputClass =
    'w-full px-3 py-3 text-xl text-center font-mono bg-[var(--color-surface-soft)] border-2 border-[var(--color-border,#d1d5db)] rounded-2xl focus:border-[var(--green-700,#1B6A41)] outline-none';

  return (
    <Sheet open={open} onClose={onClose} title={t('endReadingTitle', { defaultValue: 'End reading (garage in)' })}>
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('endReadingHint', {
            defaultValue: 'Odometer and time when you reached the garage after dropping the passenger. Used for the duty slip and billing.',
          })}
        </p>
        <label className="block">
          <span className="block text-sm font-medium text-[var(--color-text)] mb-2">
            {t('end_km_label', { defaultValue: 'End KM' })}
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={km}
            onChange={(e) => setKm(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            className={`${inputClass} tracking-widest [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            autoFocus
          />
          {kmOrderingError && release.km != null ? (
            <p className="mt-1.5 text-xs text-[var(--color-danger,#C13834)]">
              {t('end_km_too_low', { defaultValue: 'Must be at least the release odometer' })} ({release.km.toLocaleString('en-IN')} km).
            </p>
          ) : release.km != null ? (
            <p className="mt-1.5 text-xs text-[var(--color-text-secondary)]">
              {t('end_km_hint', { defaultValue: 'Release odometer' })}: {release.km.toLocaleString('en-IN')} km
            </p>
          ) : null}
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-[var(--color-text)] mb-2">
            {t('end_date_label', { defaultValue: 'End date' })}
          </span>
          <DateField value={date} onChange={setDate} min={release.date || undefined} max={todayIso()} />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-[var(--color-text)] mb-2">
            {t('end_time_label', { defaultValue: 'End time' })}
          </span>
          <TimeField value={time} onChange={setTime} />
          {timeOrderingError ? (
            <p className="mt-1.5 text-xs text-[var(--color-danger,#C13834)]">
              {t('end_before_release', { defaultValue: "End can't be before the release" })} ({release.time}).
            </p>
          ) : null}
        </label>
        <PhotoCapture
          value={dutySlipUrl}
          onChange={setDutySlipUrl}
          label={t('slip_photo_optional', { defaultValue: 'Duty slip photo (optional)' })}
        />
        <Button onClick={handleSubmit} disabled={!isValid} loading={submitting} className="w-full">
          {t('saveEndReading', { defaultValue: 'Save end reading' })}
        </Button>
      </div>
    </Sheet>
  );
}
